/* ============================================================================
   URBANSCAPE v3 — behaviour for the "Fix-it" sections
   - hero rotating word + 3D tilt on the ticket stack + stat count-up
   - trending tabs (ARIA tablist)
   - bundle duos: one-click add-both → refreshes the Refresh cart drawer
   - mobile tab bar: active state, cart open, live count
   Re-initialises on Shopify theme-editor section loads.
   ========================================================================== */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- hero: rotating word ---------- */
  function initSwap(root) {
    root.querySelectorAll('[data-us-swap]').forEach((swap) => {
      if (swap.dataset.usReady) return;
      swap.dataset.usReady = '1';
      const words = Array.from(swap.children);
      if (words.length < 2 || reduceMotion) return;
      let i = words.findIndex((w) => w.classList.contains('is-active'));
      if (i < 0) {
        i = 0;
        words[0].classList.add('is-active');
      }
      setInterval(() => {
        const current = words[i];
        i = (i + 1) % words.length;
        const next = words[i];
        current.classList.remove('is-active');
        current.classList.add('is-out');
        next.classList.remove('is-out');
        next.classList.add('is-active');
        setTimeout(() => current.classList.remove('is-out'), 620);
      }, 2600);
    });
  }

  /* ---------- hero: pointer tilt on the ticket stack ---------- */
  function initTilt(root) {
    root.querySelectorAll('[data-us-tilt]').forEach((stack) => {
      if (stack.dataset.usReady) return;
      stack.dataset.usReady = '1';
      if (!canHover || reduceMotion) return;
      const tickets = () => stack.querySelectorAll('.us-ticket');
      stack.addEventListener('pointermove', (e) => {
        const r = stack.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        tickets().forEach((t, idx) => {
          const k = 1 + idx * 0.35;
          t.style.setProperty('--ty', `${(px * 12 * k).toFixed(2)}deg`);
          t.style.setProperty('--tx', `${(-py * 12 * k).toFixed(2)}deg`);
        });
      });
      stack.addEventListener('pointerleave', () => {
        tickets().forEach((t) => {
          t.style.setProperty('--ty', '0deg');
          t.style.setProperty('--tx', '0deg');
        });
      });
    });
  }

  /* ---------- hero: count-up on numeric stats ---------- */
  function initCountUp(root) {
    const els = Array.from(root.querySelectorAll('[data-us-count]')).filter((el) => !el.dataset.usReady);
    if (!els.length) return;
    els.forEach((el) => (el.dataset.usReady = '1'));
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          const raw = String(el.dataset.usCount || el.textContent).trim();
          const target = parseInt(raw.replace(/[^\d]/g, ''), 10);
          if (!Number.isFinite(target) || !/^\d+$/.test(raw)) return;
          const start = performance.now();
          const dur = 1100;
          const tick = (now) => {
            const p = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            el.textContent = Math.round(target * eased).toString();
            if (p < 1) requestAnimationFrame(tick);
            else el.textContent = raw;
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- cascade reveal + scroll parallax ----------
     Rails that used to scroll sideways on a phone now stack downward. Each
     child reveals with a stagger as the page is swiped, and carries a small
     scroll-linked offset written to `--us-py`. That lands on the standalone
     `translate` property, so it composes with the reveal `transform` instead
     of overwriting it and never triggers layout. */
  const parallaxItems = [];
  let parallaxQueued = false;

  const parallaxOn = () => window.matchMedia('(max-width: 749px)').matches;

  function paintParallax() {
    parallaxQueued = false;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const on = parallaxOn();
    parallaxItems.forEach((entry) => {
      const { el, list, amount, depth, axis } = entry;
      if (!el.isConnected) return;
      if (!on) {
        el.style.removeProperty('--us-py');
        el.style.removeProperty('--us-px');
        list.classList.remove('is-parallax');
        return;
      }
      const r = el.getBoundingClientRect();
      if (r.bottom < -120 || r.top > vh + 120) return;
      if (axis === 'x') {
        // A rail is swiped sideways, so its drift follows the same axis:
        // -1 (still off to the right) .. 1 (swiped away past the left edge).
        const p = Math.max(-1, Math.min(1, (r.left + r.width / 2 - vw / 2) / (vw / 2)));
        el.style.setProperty('--us-px', `${(p * amount * depth).toFixed(2)}px`);
      } else {
        // -1 (entering from the bottom) .. 1 (leaving past the top)
        const p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - vh / 2) / (vh / 2)));
        el.style.setProperty('--us-py', `${(p * amount * depth).toFixed(2)}px`);
      }
      list.classList.add('is-parallax');
    });
  }

  function queueParallax() {
    if (parallaxQueued) return;
    parallaxQueued = true;
    requestAnimationFrame(paintParallax);
  }

  let parallaxBound = false;
  function bindParallax() {
    if (parallaxBound) return;
    parallaxBound = true;
    window.addEventListener('scroll', queueParallax, { passive: true });
    window.addEventListener('resize', queueParallax, { passive: true });
    window.addEventListener('orientationchange', queueParallax, { passive: true });
  }

  function initCascade(root) {
    root.querySelectorAll('[data-us-cascade]').forEach((list) => {
      if (list.dataset.usReady) return;
      list.dataset.usReady = '1';

      const items = Array.from(list.children);
      if (!items.length) return;

      // No IntersectionObserver or the visitor asked for less motion: leave the
      // markup untouched so everything is simply visible.
      if (reduceMotion || !('IntersectionObserver' in window)) return;

      list.classList.add('is-ready');

      const amount = parseFloat(list.dataset.usParallax || '0');
      const axis = list.dataset.usParallaxAxis === 'x' ? 'x' : 'y';
      items.forEach((el, i) => {
        if (!el.style.getPropertyValue('--i')) el.style.setProperty('--i', String(i));
        if (amount > 0) {
          // Alternate the depth so neighbours drift at different rates.
          parallaxItems.push({ el, list, amount, axis, depth: 0.55 + 0.45 * ((i % 3) / 2) });
        }
      });

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      items.forEach((el) => io.observe(el));

      if (amount > 0) {
        bindParallax();
        // A rail scrolls itself; the window listener alone would never fire.
        if (axis === 'x') list.addEventListener('scroll', queueParallax, { passive: true });
        queueParallax();
      }
    });
  }

  /* ---------- trending tabs ---------- */
  function initTabs(root) {
    root.querySelectorAll('[data-us-tabs]').forEach((box) => {
      if (box.dataset.usReady) return;
      box.dataset.usReady = '1';
      const tabs = Array.from(box.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      const panelFor = (tab) => box.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
      const activate = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          const p = panelFor(t);
          if (p) p.hidden = !on;
        });
        if (focus) tab.focus();
        // The rail wraps into rows on a phone; scrolling a wrapped rail would
        // yank the whole page, so only recentre while it really does scroll.
        const rail = tab.parentElement;
        if (rail && rail.scrollWidth > rail.clientWidth + 1) {
          tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      };
      tabs.forEach((tab) => tab.addEventListener('click', () => activate(tab, false)));
      box.querySelector('[role="tablist"]').addEventListener('keydown', (e) => {
        const idx = tabs.indexOf(document.activeElement);
        if (idx < 0) return;
        let next = null;
        if (e.key === 'ArrowRight') next = tabs[(idx + 1) % tabs.length];
        if (e.key === 'ArrowLeft') next = tabs[(idx - 1 + tabs.length) % tabs.length];
        if (e.key === 'Home') next = tabs[0];
        if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          activate(next, true);
        }
      });
    });
  }

  /* ---------- bundle duos: add both ---------- */
  function initDuos(root) {
    root.querySelectorAll('[data-us-duo]').forEach((btn) => {
      if (btn.dataset.usReady) return;
      btn.dataset.usReady = '1';
      btn.addEventListener('click', async () => {
        if (btn.disabled || btn.classList.contains('is-loading')) return;
        let items;
        try {
          items = JSON.parse(btn.dataset.items || '[]');
        } catch (err) {
          items = [];
        }
        if (!items.length) return;

        const label = btn.textContent;
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart && typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender().map((s) => s.id) : [];

        btn.classList.add('is-loading');
        btn.textContent = btn.dataset.labelBusy || 'Adding…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add');

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
          } else if (window.routes && window.routes.cart_url) {
            window.location.href = window.routes.cart_url;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'urbanscape-duo', productVariantId: items[0].id, cartData: data });
          }
          btn.classList.remove('is-loading');
          btn.classList.add('is-done');
          btn.textContent = btn.dataset.labelDone || 'Added ✓';
          setTimeout(() => {
            btn.classList.remove('is-done');
            btn.textContent = label;
          }, 2600);
        } catch (err) {
          btn.classList.remove('is-loading');
          btn.textContent = 'Try again';
          setTimeout(() => (btn.textContent = label), 2200);
        }
      });
    });
  }

  /* ---------- mobile tab bar ---------- */
  function initTabbar(root) {
    const bar = root.querySelector('[data-us-tabbar]');
    if (!bar || bar.dataset.usReady) return;
    bar.dataset.usReady = '1';

    const path = window.location.pathname;
    let active = 'home';
    if (/\/collections|\/products|\/search/.test(path)) active = 'shop';
    if (/\/cart/.test(path)) active = 'cart';
    if (/\/account/.test(path)) active = 'account';
    if (window.location.hash === '#finder') active = 'finder';
    bar.querySelectorAll('[data-us-tab]').forEach((el) => el.classList.toggle('is-active', el.dataset.usTab === active));

    bar.querySelector('[data-us-open-cart]')?.addEventListener('click', () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    });

    // Keep the badge in sync with the header bubble Refresh re-renders after every cart change.
    const countEl = bar.querySelector('[data-us-cart-count]');
    const bubble = document.getElementById('cart-icon-bubble');
    if (!countEl || !bubble) return;
    const sync = () => {
      const n = bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      const value = n ? n.textContent.trim() : '0';
      countEl.textContent = value;
      countEl.dataset.count = value === '' ? '0' : value;
    };
    new MutationObserver(sync).observe(bubble, { childList: true, subtree: true, characterData: true });
    sync();
  }

  function init(root = document) {
    initSwap(root);
    initTilt(root);
    initCountUp(root);
    initTabs(root);
    initCascade(root);
    initDuos(root);
    initTabbar(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target || document));
})();
